import type { CommentOutput } from "@orsane/schema/comments";
import { useEffect, useRef } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { cn } from "@/lib/utils";

export default function ChatMessage({ message, currentUserId }: { message: CommentOutput; currentUserId: string }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const tippyInstancesRef = useRef<TippyInstance[]>([]);

    const isCurrentUser = message.user.id === currentUserId;

    useEffect(() => {
        // Cleanup previous instances
        for (const instance of tippyInstancesRef.current) {
            instance.destroy();
        }
        tippyInstancesRef.current = [];

        if (!containerRef.current) return;

        // Wait for next tick to ensure DOM is updated
        const timeoutId = setTimeout(() => {
            if (!containerRef.current) return;

            // Find all mention elements with email attributes
            const mentions = containerRef.current.querySelectorAll<HTMLElement>(".mention--user[data-email]");

            mentions.forEach((mention) => {
                const email = mention.getAttribute("data-email");
                if (!email) {
                    return;
                }

                // Create tippy tooltip for each mention
                const instance = tippy(mention, {
                    content: email,
                    placement: "top",
                    delay: [300, 0],
                    arrow: true,
                    theme: "mention",
                });

                tippyInstancesRef.current.push(instance);
            });
        }, 500); // only do this after 500ms waiting time. that's ok

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            for (const instance of tippyInstancesRef.current) {
                instance.destroy();
            }
            tippyInstancesRef.current = [];
        };
    }, [message]);

    return (
        <div
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: message.comment }}
            className={cn(
                "chat-message bg-zinc-200/60 px-2 py-1.5 rounded-lg text-orsane-secondary w-fit",
                isCurrentUser && "bg-blue-200/50",
                "[&>p]:min-h-[1rem]" // Ensure all direct child <p> tags have min-height of 1rem
            )}
        />
    );
}
